import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Alert,
} from '@mui/material';
import {
  AccessTime,
  EmojiEvents,
  PlayArrow,
  ArrowBack,
  Assignment,
  CheckCircle,
  Info,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { StandaloneExam, StandaloneExamPurchase } from '../types';
import { apiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import Breadcrumb from '../components/Common/Breadcrumb';
import StandaloneExamPurchaseModal from '../components/Course/StandaloneExamPurchaseModal';
import FreeExamEnrollModal from '../components/Course/FreeExamEnrollModal';

const StandaloneExamDetail: React.FC = () => {
  const { hashed } = useParams<{ hashed: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [exam, setExam] = useState<StandaloneExam | null>(null);
  const [myPurchases, setMyPurchases] = useState<StandaloneExamPurchase[]>([]);
  const [loading, setLoading] = useState(true);
  const [purchaseModalOpen, setPurchaseModalOpen] = useState(false);
  const [freeEnrollModalOpen, setFreeEnrollModalOpen] = useState(false);
  const [lastSubmittedAttemptId, setLastSubmittedAttemptId] = useState<number | null>(null);
  const [activeAttemptId, setActiveAttemptId] = useState<number | null>(null);
  const [hasInProgressAttempt, setHasInProgressAttempt] = useState(false);
  const [hasPassedStandaloneExam, setHasPassedStandaloneExam] = useState(false);
  const [attemptsUsed, setAttemptsUsed] = useState(0);

  const fetchExam = useCallback(async (showLoader: boolean = true) => {
      if (!hashed) return;
      try {
        if (showLoader) {
          setLoading(true);
        }
        const [examData, purchasesData] = await Promise.all([
          apiService.getStandaloneExam(hashed),
          isAuthenticated ? apiService.getMyStandaloneExamPurchases().catch(() => []) : Promise.resolve([]),
        ]);
        setExam(examData);
        setMyPurchases(Array.isArray(purchasesData) ? purchasesData : []);

        // If user is authenticated and exam loaded, fetch their previous attempts for this exam
        if (examData && isAuthenticated) {
          try {
            const attempts = await apiService.getMyStandaloneExamAttemptsForExam(examData.id);
            const activeAttempt = attempts.find((a) => !a.submitted_at) || null;
            setHasInProgressAttempt(Boolean(activeAttempt));
            setActiveAttemptId(activeAttempt ? activeAttempt.id : null);

            const submitted = attempts.filter((a) => a.submitted_at);
            setAttemptsUsed(submitted.length);
            if (submitted.length > 0) {
              submitted.sort(
                (a, b) =>
                  new Date(b.submitted_at as string).getTime() -
                  new Date(a.submitted_at as string).getTime()
              );
              setLastSubmittedAttemptId(submitted[0].id);
              setHasPassedStandaloneExam(submitted.some((a) => Boolean(a.is_passed)));
            } else {
              setLastSubmittedAttemptId(null);
              setHasPassedStandaloneExam(false);
            }
          } catch (e) {
            console.error('Failed to load standalone attempts for exam', e);
            setLastSubmittedAttemptId(null);
            setActiveAttemptId(null);
            setHasInProgressAttempt(false);
            setHasPassedStandaloneExam(false);
          }
        } else {
          setLastSubmittedAttemptId(null);
          setActiveAttemptId(null);
          setHasInProgressAttempt(false);
          setHasPassedStandaloneExam(false);
        }
      } catch (error) {
        console.error('Error fetching standalone exam:', error);
        setExam(null);
      } finally {
        if (showLoader) {
          setLoading(false);
        }
      }
  }, [hashed, isAuthenticated]);

  useEffect(() => {
    fetchExam(true);
  }, [fetchExam]);

  if (loading) return <LoadingSpinner message="Loading exam..." />;
  if (!exam) return <Alert severity="error">Exam not found.</Alert>;

  // Access: purchase-first (matches backend check_user_standalone_exam_access)
  const examPurchases = myPurchases.filter(
    (p) => (p.standalone_exam_id === exam.id) || (p.standalone_exam?.id === exam.id)
  );
  const hasActiveAccess =
    examPurchases.length > 0 &&
    examPurchases.some((p) => p.is_verified && p.is_active && !p.is_expired);
  const hasEverPurchased = examPurchases.some((p) => p.is_verified);
  const hasExpiredAccess = hasEverPurchased && !hasActiveAccess;
  const isComingSoon = Boolean(exam.coming_soon);
  const isAttemptLimitReached =
    !activeAttemptId &&
    exam.max_attempts != null &&
    attemptsUsed >= exam.max_attempts;
  const canStartExam =
    !isAttemptLimitReached &&
    (!exam.grants_certificate || !hasPassedStandaloneExam) &&
    (!isComingSoon || hasInProgressAttempt);

  const heroBackground = exam.thumbnail || undefined;

  return (
    <Box>
      <Box
        sx={{
          position: 'relative',
          height: { xs: '400px', md: '500px' },
          background: heroBackground
            ? `url(${heroBackground})`
            : 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.8) 0%, rgba(30, 41, 59, 0.6) 100%)',
            zIndex: 1,
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '100px',
            background: 'linear-gradient(to bottom, transparent 0%, rgba(15, 23, 42, 1) 100%)',
            zIndex: 2,
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 3, height: '100%' }}>
          <Box sx={{ pt: 6 }}>
            <Breadcrumb
              items={[
                { label: 'Home', path: '/' },
                { label: 'Exams', path: '/exams' },
                { label: exam.title },
              ]}
            />
          </Box>
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              p: 4,
              color: 'white',
            }}
          >
            <Typography variant="h2" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
              {exam.title}
            </Typography>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 8 }}>
        {isComingSoon && (
          <Alert severity="warning" icon={<Info />} sx={{ mb: 3, borderRadius: 2 }}>
            This exam is coming soon. Purchase and new exam attempts are unavailable until it goes live.
            {hasInProgressAttempt &&
              ' You can continue an exam you already started.'}
          </Alert>
        )}
        <Box sx={{ mb: 4 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/exams')}
            sx={{ borderRadius: 2 }}
          >
            Back to Exams
          </Button>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 6, mt: 4 }}>
          {/* Left column: About This Exam */}
          <Box sx={{ flex: '2 1 0', minWidth: 0 }}>
            <Box sx={{ mb: 6 }}>
              <Typography variant="h4" sx={{ fontWeight: 600, mb: 3, color: 'text.primary' }}>
                About This Exam
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1.5,
                  mb: 3,
                  p: 2,
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.06) 0%, rgba(34, 197, 94, 0.04) 100%)',
                  border: '1px solid rgba(16, 185, 129, 0.15)',
                }}
              >
                {exam.duration_minutes != null && (
                  <Chip
                    icon={<AccessTime sx={{ fontSize: '1.1rem !important' }} />}
                    label={`${exam.duration_minutes} min`}
                    size="medium"
                    sx={{
                      borderRadius: 2,
                      height: 32,
                      fontWeight: 600,
                      bgcolor: 'rgba(59, 130, 246, 0.12)',
                      color: 'primary.main',
                      border: '1px solid rgba(59, 130, 246, 0.25)',
                      '& .MuiChip-icon': { color: 'inherit' },
                    }}
                  />
                )}
                {exam.grants_certificate && (
                  <Chip
                    icon={<EmojiEvents sx={{ fontSize: '1.1rem !important' }} />}
                    label="Certificate"
                    size="medium"
                    sx={{
                      borderRadius: 2,
                      height: 32,
                      fontWeight: 600,
                      background: 'linear-gradient(135deg, #10b981 0%, #22c55e 100%)',
                      color: '#fff',
                      boxShadow: '0 2px 8px rgba(16, 185, 129, 0.35)',
                      '& .MuiChip-icon': { color: 'inherit' },
                    }}
                  />
                )}
                <Chip
                  icon={<Assignment sx={{ fontSize: '1.1rem !important' }} />}
                  label={`${exam.questions_count} questions`}
                  size="medium"
                  variant="outlined"
                  sx={{
                    borderRadius: 2,
                    height: 32,
                    fontWeight: 600,
                    borderColor: 'rgba(16, 185, 129, 0.4)',
                    color: 'text.primary',
                    '& .MuiChip-icon': { color: '#10b981' },
                  }}
                />
                {exam.passing_score != null && (
                  <Chip
                    label={`Passing: ${exam.passing_score}%`}
                    size="medium"
                    variant="outlined"
                    sx={{
                      borderRadius: 2,
                      height: 32,
                      fontWeight: 600,
                      borderColor: 'rgba(245, 158, 11, 0.5)',
                      color: 'warning.dark',
                      bgcolor: 'rgba(245, 158, 11, 0.08)',
                    }}
                  />
                )}
              </Box>
              {exam.description ? (
                <Box
                  sx={{
                    fontSize: '1.1rem',
                    lineHeight: 1.8,
                    color: 'text.secondary',
                    maxWidth: '800px',
                    '& p': { mb: 2 },
                    '& img': { maxWidth: '100%', height: 'auto', borderRadius: 2 },
                    '& ul, & ol': { pl: 3, mb: 2 },
                  }}
                  dangerouslySetInnerHTML={{ __html: exam.description }}
                />
              ) : (
                <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
                  Test your skills with this standalone assessment. Purchase access to unlock the exam.
                </Typography>
              )}
            </Box>
          </Box>

          {/* Right column: Sticky price / CTA card (like CourseDetail) */}
          <Box sx={{ flex: '1 1 0', minWidth: '300px' }}>
            {!isAuthenticated && (
              <Box
                sx={{
                  p: 4,
                  borderRadius: 1,
                  background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.8) 0%, rgba(30, 41, 59, 0.6) 100%)',
                  border: '1px solid rgba(16, 185, 129, 0.3)',
                  position: 'sticky',
                  top: 24,
                }}
              >
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
                  Ready to Take This Exam?
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 700, mb: 2, color: '#10b981' }}>
                  {exam.is_free ? 'Free' : `$${exam.price}`}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1, lineHeight: 1.6 }}>
                  {exam.questions_count} questions | Passing score {exam.passing_score}%.
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3, lineHeight: 1.6 }}>
                  {exam.is_free
                    ? 'This exam is free. Get instant access, no payment required.'
                    : 'You need to purchase this exam first before you can take it.'}
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  disabled={isComingSoon}
                  onClick={() => !isComingSoon && (exam.is_free ? setFreeEnrollModalOpen(true) : setPurchaseModalOpen(true))}
                  sx={{
                    py: 2,
                    mb: 2,
                    background: 'linear-gradient(135deg, #10b981 0%, #22c55e 100%)',
                    boxShadow: '0 8px 25px rgba(16, 185, 129, 0.3)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #059669 0%, #16a34a 100%)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 12px 35px rgba(16, 185, 129, 0.4)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  {isComingSoon ? 'Coming soon' : exam.is_free ? 'Get Free Access' : 'Purchase Exam'}
                </Button>
              </Box>
            )}

            {isAuthenticated && hasActiveAccess && (
              <Box
                sx={{
                  p: 4,
                  borderRadius: 1,
                  background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(34, 197, 94, 0.05) 100%)',
                  border: '1px solid rgba(16, 185, 129, 0.3)',
                  position: 'sticky',
                  top: 24,
                }}
              >
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                  <CheckCircle sx={{ fontSize: '3rem', color: '#10b981', mb: 2 }} />
                  <Typography variant="h5" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
                    {isAttemptLimitReached
                      ? 'No Attempts Remaining'
                      : exam.grants_certificate && hasPassedStandaloneExam
                      ? 'Exam Completed'
                      : 'You have access'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1, lineHeight: 1.6 }}>
                    {exam.questions_count} questions • Passing score {exam.passing_score}%.
                  </Typography>
                  {exam.max_attempts != null && (
                    <Typography variant="body2" sx={{ color: isAttemptLimitReached ? 'error.main' : 'text.secondary', lineHeight: 1.6, fontWeight: 600 }}>
                      {attemptsUsed} / {exam.max_attempts} attempt{exam.max_attempts !== 1 ? 's' : ''} used
                    </Typography>
                  )}
                  {exam.grants_certificate && hasPassedStandaloneExam && (
                    <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                      You already passed this certificate exam. New attempts are disabled.
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {activeAttemptId && (
                    <Button
                      variant="contained"
                      size="large"
                      fullWidth
                      startIcon={<PlayArrow />}
                      onClick={() => navigate(`/exams/${exam.hashed}/take`)}
                      sx={{
                        py: 2,
                        background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                        boxShadow: '0 8px 25px rgba(245, 158, 11, 0.3)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 12px 35px rgba(245, 158, 11, 0.4)',
                        },
                        transition: 'all 0.3s ease',
                      }}
                    >
                      Resume Exam
                    </Button>
                  )}
                  {isAttemptLimitReached && (
                    <Alert severity="error" sx={{ borderRadius: 2, textAlign: 'left' }}>
                      You have used all {exam.max_attempts} attempt{exam.max_attempts !== 1 ? 's' : ''} for this exam.
                    </Alert>
                  )}
                  {canStartExam && !activeAttemptId && (
                    <Button
                      variant="contained"
                      size="large"
                      fullWidth
                      onClick={() => navigate(`/exams/${exam.hashed}/take`)}
                      sx={{
                        py: 2,
                        background: 'linear-gradient(135deg, #10b981 0%, #22c55e 100%)',
                        boxShadow: '0 8px 25px rgba(16, 185, 129, 0.3)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #059669 0%, #16a34a 100%)',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 12px 35px rgba(16, 185, 129, 0.4)',
                        },
                        transition: 'all 0.3s ease',
                      }}
                    >
                      {lastSubmittedAttemptId ? 'Try Again' : 'Start Exam'}
                    </Button>
                  )}
                  {lastSubmittedAttemptId && (
                    <Button
                      variant="outlined"
                      size="large"
                      fullWidth
                      onClick={() => navigate(`/exams/${exam.hashed}/result/${lastSubmittedAttemptId}`)}
                      sx={{
                        py: 2,
                        borderRadius: 2,
                        fontWeight: 600,
                        borderWidth: 2,
                        borderColor: 'rgba(148, 163, 184, 0.8)',
                        color: 'text.primary',
                        '&:hover': {
                          backgroundColor: 'rgba(148, 163, 184, 0.12)',
                          borderColor: '#e5e7eb',
                        },
                      }}
                    >
                      See Results
                    </Button>
                  )}
                </Box>
              </Box>
            )}

            {isAuthenticated && !hasActiveAccess && (
              <Box
                sx={{
                  p: 4,
                  borderRadius: 1,
                  background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.8) 0%, rgba(30, 41, 59, 0.6) 100%)',
                  border: '1px solid rgba(16, 185, 129, 0.3)',
                  position: 'sticky',
                  top: 24,
                }}
              >
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
                  {exam.is_free ? 'Ready to Take This Exam?' : hasExpiredAccess ? 'Access Expired' : 'Ready to Take This Exam?'}
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 700, mb: 2, color: '#10b981' }}>
                  {exam.is_free ? 'Free' : `$${exam.price}`}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1, lineHeight: 1.6 }}>
                  {exam.questions_count} questions • Passing score {exam.passing_score}%.
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3, lineHeight: 1.6 }}>
                  {exam.is_free
                    ? 'This exam is free. Get instant access, no payment required.'
                    : hasExpiredAccess
                    ? 'Your access to this exam has expired. Renew to take the exam again.'
                    : 'Purchase access to unlock this exam. After your payment is verified, you can start your attempt.'}
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  disabled={isComingSoon}
                  onClick={() => !isComingSoon && (exam.is_free ? setFreeEnrollModalOpen(true) : setPurchaseModalOpen(true))}
                  sx={{
                    py: 2,
                    mb: 2,
                    background: 'linear-gradient(135deg, #10b981 0%, #22c55e 100%)',
                    boxShadow: '0 8px 25px rgba(16, 185, 129, 0.3)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #059669 0%, #16a34a 100%)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 12px 35px rgba(16, 185, 129, 0.4)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  {isComingSoon ? 'Coming soon' : exam.is_free ? 'Get Free Access' : hasExpiredAccess ? 'Renew Access' : 'Purchase This Exam'}
                </Button>
              </Box>
            )}

            <StandaloneExamPurchaseModal
              open={purchaseModalOpen}
              onClose={() => setPurchaseModalOpen(false)}
              exam={exam}
            />

            <FreeExamEnrollModal
              open={freeEnrollModalOpen}
              onClose={() => setFreeEnrollModalOpen(false)}
              exam={exam}
              onEnrolled={() => {
                void fetchExam(false);
              }}
            />
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default StandaloneExamDetail;
